/**
 * Look up a coverage-page string in the reader's language.
 *
 * The translations are MACHINE-MADE once, at build time, by scripts/translate_coverage.py and committed as
 * coverage.i18n.generated.json — so the pages follow the site's language selector (a browser translate bar
 * never does) and the no-network deploy gate needs nothing. Traditional Chinese is derived from Simplified
 * through the opencc chokepoint, exactly as the hand-written copy is. A string with no translation falls
 * back to English rather than to nothing; the page says it is machine-translated (COVERAGE_UI.machineTranslated).
 */
import generated from './coverage.i18n.generated.json'
import { getMyCIOLanguage, type MyCIOLanguageCode } from './language'
import { toTraditionalDeep } from './zhHant'
import { COVERAGE_UI, type CoverageUiKey } from './coverageStrings'

type Dict = Record<string, string>
const TABLE = generated as Record<string, Dict>

function dictFor(code: MyCIOLanguageCode): Dict | null {
  if (code === 'en') return null
  if (code === 'zh-Hant') {
    const zh = TABLE['zh']
    return zh ? (toTraditionalDeep(zh) as Dict) : null
  }
  return TABLE[code] ?? null
}

export type CoverageT = {
  /** Translate any English sentence the pages show (data or chrome); English when no translation exists. */
  s: (english: string, vars?: Record<string, string>) => string
  /** Translate a chrome key from COVERAGE_UI. */
  ui: (key: CoverageUiKey, vars?: Record<string, string>) => string
  /** True when anything on the page is machine-translated (i.e. the language is not English). */
  machine: boolean
}

function fill(text: string, vars?: Record<string, string>): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (_m, k: string) => (k in vars ? vars[k] : `{${k}}`))
}

export function coverageT(language: string | null | undefined): CoverageT {
  const code = getMyCIOLanguage(language).code
  const dict = dictFor(code)
  const s = (english: string, vars?: Record<string, string>) => fill(dict?.[english] ?? english, vars)
  return {
    s,
    ui: (key, vars) => s(COVERAGE_UI[key], vars),
    machine: dict !== null,
  }
}
